import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { companyInfo } from '../utils/dataInfo';

// Helper functions
const formatCurrency = (amount) => {
return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
}).format(amount || 0);
};

const formatNumber = (num) => {
return new Intl.NumberFormat('en-US').format(num || 0);
};

const formatDate = (date) => {
return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
});
};

const getStockLevelColor = (level) => {
switch (level?.toLowerCase()) {
    case 'out of stock': return '#f44336';
    case 'low': return '#ff9800';
    case 'normal': return '#4caf50';
    case 'high': return '#2196f3';
    default: return '#757575';
}
};

// Generate Excel Report
export const exportStockReportToExcel = async (reportData, filters = {}) => {
try {
    const workbook = XLSX.utils.book_new();
    
    // Summary Sheet
    const summarySheetData = [
    ['STOCK REPORT SUMMARY'],
    ['Generated on', new Date().toLocaleString()],
    [''],
    ['Total Items', reportData.summary.total_items],
    ['Total Value', reportData.summary.total_value],
    ['Average Profit Margin', `${reportData.summary.avg_profit_margin?.toFixed(2)}%`],
    ['Active Items', reportData.summary.active_items],
    ['Inactive Items', reportData.summary.inactive_items],
    ['Out of Stock Items', reportData.summary.out_of_stock_items],
    ['Low Stock Items', reportData.summary.low_stock_items],
    ['Normal Stock Items', reportData.summary.normal_stock_items],
    ['High Stock Items', reportData.summary.high_stock_items],
    [''],
    ['COST ANALYSIS'],
    ['Minimum Cost', reportData.summary.min_cost],
    ['Maximum Cost', reportData.summary.max_cost],
    ['Average Cost', reportData.summary.avg_cost],
    [''],
    ['SELLING ANALYSIS'],
    ['Minimum Selling', reportData.summary.min_selling],
    ['Maximum Selling', reportData.summary.max_selling],
    ['Average Selling', reportData.summary.avg_selling],
    [''],
    ['DATE RANGE'],
    ['Oldest Stock', reportData.summary.oldest_stock ? formatDate(reportData.summary.oldest_stock) : 'N/A'],
    ['Newest Stock', reportData.summary.newest_stock ? formatDate(reportData.summary.newest_stock) : 'N/A']
    ];

    const summarySheet = XLSX.utils.aoa_to_sheet(summarySheetData);
    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');

    // Details Sheet
    const detailsHeaders = [
    'Item Name',
    'Counter',
    'Stock Level',
    'Balance',
    'Cost Price',
    'Selling Price',
    'Total Value',
    'Profit Margin',
    'Status',
    'Stock Date',
    'UOM'
    ];

    const detailsData = reportData.details?.map(item => [
    item.name,
    item.counter_name || `Counter ${item.counter_id}`,
    item.stock_level,
    item.balance,
    item.cost,
    item.selling,
    item.total_value || (item.balance * item.cost),
    `${((item.selling - item.cost) / item.cost * 100).toFixed(2)}%`,
    item.status === 1 ? 'Active' : 'Inactive',
    formatDate(item.stock_date),
    item.uom
    ]) || [];

    const detailsSheetData = [detailsHeaders, ...detailsData];
    const detailsSheet = XLSX.utils.aoa_to_sheet(detailsSheetData);
    XLSX.utils.book_append_sheet(workbook, detailsSheet, 'Details');

    // Trends Sheet
    if (reportData.trends?.length > 0) {
    const trendsHeaders = ['Month', 'Item Count', 'Total Balance', 'Total Value', 'Avg Profit Margin'];
    const trendsData = reportData.trends.map(trend => [
        trend.month,
        trend.item_count,
        trend.total_balance,
        trend.total_value,
        `${trend.avg_profit_margin?.toFixed(2)}%`
    ]);
    
    const trendsSheetData = [trendsHeaders, ...trendsData];
    const trendsSheet = XLSX.utils.aoa_to_sheet(trendsSheetData);
    XLSX.utils.book_append_sheet(workbook, trendsSheet, 'Trends');
    }

    // Top Items Sheet
    if (reportData.topItems?.length > 0) {
    const topItemsHeaders = ['Rank', 'Item Name', 'Total Value', 'Balance', 'Unit Cost', 'Unit Selling', 'Profit Margin'];
    const topItemsData = reportData.topItems.map((item, index) => [
        index + 1,
        item.name,
        item.total_value || (item.balance * item.cost),
        item.balance,
        item.cost,
        item.selling,
        `${((item.selling - item.cost) / item.cost * 100).toFixed(2)}%`
    ]);
    
    const topItemsSheetData = [topItemsHeaders, ...topItemsData];
    const topItemsSheet = XLSX.utils.aoa_to_sheet(topItemsSheetData);
    XLSX.utils.book_append_sheet(workbook, topItemsSheet, 'Top Items');
    }

    // Set column widths
    const sheets = workbook.SheetNames;
    sheets.forEach(sheetName => {
    const worksheet = workbook.Sheets[sheetName];
    const range = XLSX.utils.decode_range(worksheet['!ref']);
    const colWidths = [];
    
    for (let col = range.s.c; col <= range.e.c; col++) {
        let maxWidth = 0;
        for (let row = range.s.r; row <= range.e.r; row++) {
        const cell = worksheet[XLSX.utils.encode_cell({ r: row, c: col })];
        if (cell && cell.v) {
            const cellWidth = String(cell.v).length;
            maxWidth = Math.max(maxWidth, cellWidth);
        }
        }
        colWidths.push({ wch: Math.min(maxWidth + 2, 50) });
    }
    
    worksheet['!cols'] = colWidths;
    });

    // Generate file name
    const fileName = `stock_report_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);

    return { success: true, fileName };
} catch (error) {
    console.error('Excel export failed:', error);
    throw new Error('Failed to export to Excel: ' + error.message);
}
};

// Generate PDF Report
export const exportStockReportToPDF = async (reportData, filters = {}) => {
try {
    const doc = new jsPDF('landscape');
    
    // Set document properties
    doc.setProperties({
    title: 'Stock Analysis Report',
    subject: 'Comprehensive Stock Inventory Report',
    author: companyInfo.name,
    keywords: 'stock, inventory, report, analysis',
    creator: companyInfo.name
    });

    // Title Page
    doc.setFontSize(24);
    doc.setTextColor(33, 150, 243);
    doc.text('STOCK ANALYSIS REPORT', 105, 50, { align: 'center' });
    
    doc.setFontSize(16);
    doc.setTextColor(66, 66, 66);
    doc.text(companyInfo.name, 105, 65, { align: 'center' });
    
    doc.setFontSize(12);
    doc.setTextColor(97, 97, 97);
    doc.text(`Generated on: ${new Date().toLocaleString()}`, 105, 75, { align: 'center' });
    
    if (filters.dateFrom && filters.dateTo) {
    doc.text(`Period: ${formatDate(filters.dateFrom)} to ${formatDate(filters.dateTo)}`, 105, 82, { align: 'center' });
    }

    // Summary Section
    doc.addPage();
    doc.setFontSize(18);
    doc.setTextColor(33, 150, 243);
    doc.text('EXECUTIVE SUMMARY', 20, 20);

    doc.setFontSize(11);
    doc.setTextColor(66, 66, 66);
    
    let yPos = 35;
    const summaryItems = [
    ['Total Items:', formatNumber(reportData.summary.total_items)],
    ['Total Value:', formatCurrency(reportData.summary.total_value)],
    ['Average Profit Margin:', `${reportData.summary.avg_profit_margin?.toFixed(2)}%`],
    ['Active Items:', formatNumber(reportData.summary.active_items)],
    ['Inactive Items:', formatNumber(reportData.summary.inactive_items)],
    ['Out of Stock Items:', formatNumber(reportData.summary.out_of_stock_items)],
    ['Low Stock Items:', formatNumber(reportData.summary.low_stock_items)],
    ['Normal Stock Items:', formatNumber(reportData.summary.normal_stock_items)],
    ['High Stock Items:', formatNumber(reportData.summary.high_stock_items)]
    ];

    summaryItems.forEach(([label, value], index) => {
    const x = index % 2 === 0 ? 20 : 110;
    const y = yPos + Math.floor(index / 2) * 10;
    
    doc.setTextColor(97, 97, 97);
    doc.text(label, x, y);
    doc.setTextColor(33, 33, 33);
    doc.text(value, x + 50, y);
    });

    // Stock Analysis Table
    doc.addPage();
    doc.setFontSize(18);
    doc.setTextColor(33, 150, 243);
    doc.text('DETAILED STOCK ANALYSIS', 20, 20);

    const tableData = reportData.details?.map(item => [
    item.name,
    `Counter ${item.counter_id}`,
    item.stock_level,
    formatNumber(item.balance),
    formatCurrency(item.cost),
    formatCurrency(item.selling),
    formatCurrency(item.total_value || item.balance * item.cost),
    `${((item.selling - item.cost) / item.cost * 100).toFixed(2)}%`,
    item.status === 1 ? 'Active' : 'Inactive'
    ]) || [];

    autoTable(doc, {
    startY: 30,
    head: [['Item Name', 'Counter', 'Stock Level', 'Balance', 'Cost', 'Selling', 'Total Value', 'Profit Margin', 'Status']],
    body: tableData,
    theme: 'striped',
    headStyles: { fillColor: [33, 150, 243], textColor: 255 },
    columnStyles: {
        2: { cellWidth: 'auto', halign: 'center' },
        3: { halign: 'right' },
        4: { halign: 'right' },
        5: { halign: 'right' },
        6: { halign: 'right' },
        7: { halign: 'right' },
        8: { halign: 'center' }
    },
    didDrawCell: (data) => {
        // Color code stock levels
        if (data.column.index === 2 && data.cell.raw) {
        const color = getStockLevelColor(data.cell.raw);
        doc.setTextColor(color);
        doc.setFillColor(color + '20');
        }
        
        // Color code status
        if (data.column.index === 8) {
        if (data.cell.raw === 'Active') {
            doc.setTextColor(76, 175, 80);
        } else {
            doc.setTextColor(244, 67, 54);
        }
        }
    }
    });

    // Top Items Page
    if (reportData.topItems?.length > 0) {
    doc.addPage();
    doc.setFontSize(18);
    doc.setTextColor(33, 150, 243);
    doc.text('TOP ITEMS BY VALUE', 20, 20);

    const topItemsData = reportData.topItems.slice(0, 10).map((item, index) => [
        index + 1,
        item.name,
        formatNumber(item.balance),
        formatCurrency(item.cost),
        formatCurrency(item.total_value || item.balance * item.cost),
        `${((item.selling - item.cost) / item.cost * 100).toFixed(2)}%`
    ]);

    autoTable(doc, {
        startY: 30,
        head: [['Rank', 'Item Name', 'Balance', 'Unit Cost', 'Total Value', 'Profit Margin']],
        body: topItemsData,
        theme: 'grid',
        headStyles: { fillColor: [76, 175, 80], textColor: 255 }
    });
    }

    // Low Stock Alerts
    if (reportData.lowStockItems?.length > 0) {
    doc.addPage();
    doc.setFontSize(18);
    doc.setTextColor(33, 150, 243);
    doc.text('LOW STOCK ALERTS', 20, 20);

    const lowStockData = reportData.lowStockItems.slice(0, 15).map(item => [
        item.name,
        `Counter ${item.counter_id}`,
        item.stock_level,
        formatNumber(item.balance),
        formatCurrency(item.cost),
        formatCurrency(item.selling)
    ]);

    autoTable(doc, {
        startY: 30,
        head: [['Item Name', 'Counter', 'Stock Level', 'Balance', 'Cost', 'Selling']],
        body: lowStockData,
        theme: 'striped',
        headStyles: { fillColor: [255, 152, 0], textColor: 255 },
        didDrawCell: (data) => {
        if (data.column.index === 2) {
            doc.setTextColor(255, 152, 0);
        }
        }
    });
    }

    // Footer on all pages
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(10);
    doc.setTextColor(128, 128, 128);
    doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.width - 30, doc.internal.pageSize.height - 10);
    doc.text(companyInfo.name, 20, doc.internal.pageSize.height - 10);
    }

    const fileName = `stock_report_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);

    return { success: true, fileName };
} catch (error) {
    console.error('PDF export failed:', error);
    throw new Error('Failed to export to PDF: ' + error.message);
}
};

// Generate CSV Report
export const exportStockReportToCSV = async (reportData, filters = {}) => {
try {
    // Prepare CSV data
    const csvLines = [];
    
    // Header
    csvLines.push('STOCK ANALYSIS REPORT');
    csvLines.push(`Generated on: ${new Date().toLocaleString()}`);
    csvLines.push(`Company: ${companyInfo.name}`);
    csvLines.push('');
    
    // Summary
    csvLines.push('SUMMARY');
    csvLines.push('Metric,Value');
    csvLines.push(`Total Items,${reportData.summary.total_items}`);
    csvLines.push(`Total Value,${reportData.summary.total_value}`);
    csvLines.push(`Average Profit Margin,${reportData.summary.avg_profit_margin?.toFixed(2)}%`);
    csvLines.push(`Active Items,${reportData.summary.active_items}`);
    csvLines.push(`Inactive Items,${reportData.summary.inactive_items}`);
    csvLines.push(`Out of Stock Items,${reportData.summary.out_of_stock_items}`);
    csvLines.push(`Low Stock Items,${reportData.summary.low_stock_items}`);
    csvLines.push('');
    
    // Details
    csvLines.push('DETAILED STOCK ITEMS');
    csvLines.push('Item Name,Counter,Stock Level,Balance,Cost Price,Selling Price,Total Value,Profit Margin,Status,Stock Date,UOM');
    
    reportData.details?.forEach(item => {
    const profitMargin = ((item.selling - item.cost) / item.cost * 100).toFixed(2);
    csvLines.push([
        `"${item.name.replace(/"/g, '""')}"`,
        `Counter ${item.counter_id}`,
        item.stock_level,
        item.balance,
        item.cost,
        item.selling,
        item.total_value || item.balance * item.cost,
        `${profitMargin}%`,
        item.status === 1 ? 'Active' : 'Inactive',
        formatDate(item.stock_date),
        item.uom
    ].join(','));
    });
    
    csvLines.push('');
    
    // Top Items
    if (reportData.topItems?.length > 0) {
    csvLines.push('TOP ITEMS BY VALUE');
    csvLines.push('Rank,Item Name,Total Value,Balance,Unit Cost,Unit Selling,Profit Margin');
    
    reportData.topItems.slice(0, 10).forEach((item, index) => {
        const profitMargin = ((item.selling - item.cost) / item.cost * 100).toFixed(2);
        csvLines.push([
        index + 1,
        `"${item.name.replace(/"/g, '""')}"`,
        item.total_value || item.balance * item.cost,
        item.balance,
        item.cost,
        item.selling,
        `${profitMargin}%`
        ].join(','));
    });
    }

    // Create CSV file
    const csvContent = csvLines.join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `stock_report_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    return { success: true };
} catch (error) {
    console.error('CSV export failed:', error);
    throw new Error('Failed to export to CSV: ' + error.message);
}
};

// Simple PDF Export (Quick Report)
export const exportStockReportSimplePDF = async (reportData) => {
try {
    const doc = new jsPDF();
    
    // Title
    doc.setFontSize(18);
    doc.text('Stock Quick Report', 20, 20);
    
    // Summary
    doc.setFontSize(12);
    let y = 40;
    
    const quickSummary = [
    `Total Items: ${formatNumber(reportData.summary.total_items)}`,
    `Total Value: ${formatCurrency(reportData.summary.total_value)}`,
    `Active Items: ${formatNumber(reportData.summary.active_items)}`,
    `Low Stock Items: ${formatNumber(reportData.summary.low_stock_items)}`,
    `Out of Stock: ${formatNumber(reportData.summary.out_of_stock_items)}`
    ];
    
    quickSummary.forEach(line => {
    doc.text(line, 20, y);
    y += 10;
    });
    
    y += 10;
    
    // Critical Items
    if (reportData.lowStockItems?.length > 0) {
    doc.text('Critical Stock Items:', 20, y);
    y += 10;
    
    reportData.lowStockItems.slice(0, 5).forEach(item => {
        doc.text(`• ${item.name}: ${item.balance} units (${item.stock_level})`, 25, y);
        y += 7;
    });
    }
    
    const fileName = `stock_quick_report_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
    
    return { success: true, fileName };
} catch (error) {
    console.error('Simple PDF export failed:', error);
    throw new Error('Failed to export quick PDF: ' + error.message);
}
};